import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus } from 'react-icons/fi';
import styles from '../components/bodywon.module.css';
import { normalizeAddress } from './addressHelpers';
import { createAddressApi, fetchAddressById, fetchAddresses, updateAddressApi } from '../utils/addressApi';
import { ensureLoggedInUserId, isCartUserLoggedIn } from '../utils/cartApi';

const pickDefaultAddressId = (addresses) => {
  if (!addresses.length) return null;
  const defaultAddr = addresses.find((row) => row.isDefault);
  return defaultAddr?.addressId ?? addresses[0].addressId;
};

const formatAddressSummary = (address) => {
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.landmark,
    address.city,
    address.state,
    address.postalCode,
  ].filter((part) => String(part || '').trim());
  return parts.length ? parts.join(', ') : '—';
};

const initialForm = {
  FullName: '',
  MobileNumber: '',
  AddressLine1: '',
  AddressLine2: '',
  Landmark: '',
  City: '',
  State: '',
  Country: 'India',
  PostalCode: '',
  AddressType: 'Home',
  IsDefault: true,
};

const CheckoutAddressPage = ({ onRequireLogin, mode = 'checkout' }) => {
  const isAccountMode = mode === 'account';
  const basePath = isAccountMode ? '/account/addresses' : '/checkout/address';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cartId');
  const addressId = searchParams.get('addressId');
  const isAddMode = searchParams.get('mode') === 'add';
  const isEditMode = Boolean(addressId);

  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [loadingList, setLoadingList] = useState(!isEditMode && !isAddMode);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showForm, setShowForm] = useState(isEditMode || isAddMode);

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
    }
  }, [navigate, onRequireLogin]);

  useEffect(() => {
    if (isEditMode) {
      setShowForm(true);
      setLoadingList(false);
      return;
    }

    let active = true;
    setLoadingList(true);
    setError('');

    (async () => {
      try {
        const userId = await ensureLoggedInUserId();
        if (!userId) {
          if (active) {
            setError('Please login again.');
            onRequireLogin?.();
          }
          return;
        }

        const rows = await fetchAddresses({ userId, pageSize: 50 });
        if (!active) return;

        const normalized = rows.map(normalizeAddress).filter((row) => row.addressId);
        setSavedAddresses(normalized);
        setSelectedAddressId(pickDefaultAddressId(normalized));
        setShowForm(isAddMode || normalized.length === 0);
      } catch (err) {
        if (active) setError(err.message || 'Could not load saved addresses.');
        setShowForm(true);
      } finally {
        if (active) setLoadingList(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [addressId, isAddMode, isEditMode, onRequireLogin]);

  useEffect(() => {
    if (!addressId) return;

    let active = true;
    setLoadingAddress(true);

    (async () => {
      try {
        const data = await fetchAddressById(addressId);
        if (!active || !data) return;
        setForm({
          FullName: data.FullName ?? data.fullName ?? '',
          MobileNumber: data.MobileNumber ?? data.mobileNumber ?? '',
          AddressLine1: data.AddressLine1 ?? data.addressLine1 ?? '',
          AddressLine2: data.AddressLine2 ?? data.addressLine2 ?? '',
          Landmark: data.Landmark ?? data.landmark ?? '',
          City: data.City ?? data.city ?? '',
          State: data.State ?? data.state ?? '',
          Country: data.Country ?? data.country ?? 'India',
          PostalCode: data.PostalCode ?? data.postalCode ?? '',
          AddressType: data.AddressType ?? data.addressType ?? 'Home',
          IsDefault: Boolean(data.IsDefault ?? data.isDefault),
        });
      } catch (err) {
        if (active) setError(err.message || 'Could not load address.');
      } finally {
        if (active) setLoadingAddress(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [addressId]);

  useEffect(() => {
    if (isAddMode && !isEditMode) {
      setForm(initialForm);
    }
  }, [isAddMode, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectAddress = (selectedId) => {
    if (isAccountMode) return;
    if (!cartId) {
      setError('Cart reference missing. Please start checkout again.');
      return;
    }
    navigate(`/checkout/review?cartId=${cartId}&addressId=${selectedId}`);
  };

  const handleEditAddress = (e, selectedId) => {
    e.preventDefault();
    e.stopPropagation();
    const qs = isAccountMode
      ? `?addressId=${selectedId}`
      : `?cartId=${cartId}&addressId=${selectedId}`;
    navigate(`${basePath}${qs}`);
  };

  const handleDeliverAddress = (e, selectedId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedAddressId(selectedId);
    handleSelectAddress(selectedId);
  };

  const handleAddNew = () => {
    const qs = isAccountMode ? '?mode=add' : `?cartId=${cartId}&mode=add`;
    navigate(`${basePath}${qs}`);
  };

  const handleBackFromForm = () => {
    if (savedAddresses.length > 0 && !isEditMode) {
      navigate(isAccountMode ? basePath : `${basePath}?cartId=${cartId}`);
      setShowForm(false);
      setForm(initialForm);
      setError('');
      return;
    }
    navigate(isAccountMode ? '/' : '/cart');
  };

  const reloadAddressList = async () => {
    const userId = await ensureLoggedInUserId();
    if (!userId) return;
    const rows = await fetchAddresses({ userId, pageSize: 50 });
    const normalized = rows.map(normalizeAddress).filter((row) => row.addressId);
    setSavedAddresses(normalized);
    setSelectedAddressId(pickDefaultAddressId(normalized));
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.FullName.trim()) {
      setError('Full name is required.');
      return;
    }
    if (!form.MobileNumber.trim()) {
      setError('Mobile number is required.');
      return;
    }
    if (!form.AddressLine1.trim()) {
      setError('Address line 1 is required.');
      return;
    }
    if (!form.City.trim()) {
      setError('City is required.');
      return;
    }
    if (!form.State.trim()) {
      setError('State is required.');
      return;
    }
    if (!form.Country.trim()) {
      setError('Country is required.');
      return;
    }
    if (!form.PostalCode.trim()) {
      setError('Postal code is required.');
      return;
    }
    if (!isAccountMode && !cartId) {
      setError('Cart reference missing. Please start checkout again.');
      return;
    }

    const userId = await ensureLoggedInUserId();
    if (!userId) {
      setError('Please login again.');
      onRequireLogin?.();
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await updateAddressApi({
          AddressId: Number(addressId),
          UserId: userId,
          FullName: form.FullName.trim(),
          MobileNumber: form.MobileNumber.trim(),
          AddressLine1: form.AddressLine1.trim(),
          AddressLine2: form.AddressLine2.trim() || null,
          Landmark: form.Landmark.trim() || null,
          City: form.City.trim(),
          State: form.State.trim(),
          Country: form.Country.trim(),
          PostalCode: form.PostalCode.trim(),
          AddressType: form.AddressType,
          IsDefault: form.IsDefault,
          UpdatedBy: userId,
        });
        toast.success('Address updated.');
        if (isAccountMode) {
          await reloadAddressList();
          navigate(basePath);
          return;
        }
        navigate(`/checkout/review?cartId=${cartId}&addressId=${addressId}`);
        return;
      }

      const newAddressId = await createAddressApi({
        UserId: userId,
        FullName: form.FullName.trim(),
        MobileNumber: form.MobileNumber.trim(),
        AddressLine1: form.AddressLine1.trim(),
        AddressLine2: form.AddressLine2.trim() || null,
        Landmark: form.Landmark.trim() || null,
        City: form.City.trim(),
        State: form.State.trim(),
        Country: form.Country.trim(),
        PostalCode: form.PostalCode.trim(),
        AddressType: form.AddressType,
        IsDefault: form.IsDefault,
        CreatedBy: userId,
      });
      toast.success('Address saved.');
      if (isAccountMode) {
        await reloadAddressList();
        navigate(basePath);
        return;
      }
      navigate(`/checkout/review?cartId=${cartId}&addressId=${newAddressId}`);
    } catch (err) {
      setError(err.message || 'Could not save address.');
    } finally {
      setSaving(false);
    }
  };

  if (!isCartUserLoggedIn()) return null;

  const pageTitle = isAccountMode
    ? (isEditMode ? 'Edit Address' : showForm && isAddMode ? 'Add Address' : showForm ? 'Add Address' : 'Your Addresses')
    : isEditMode
      ? 'Edit Delivery Address'
      : showForm && isAddMode
        ? 'Add Delivery Address'
        : showForm
          ? 'Delivery Address'
          : 'Select Delivery Address';

  const pageSub = isAccountMode
    ? (showForm ? 'Save your address details below.' : 'Manage your saved delivery addresses.')
    : showForm
      ? 'Enter your shipping details to continue.'
      : 'Choose a saved address or add a new one for delivery.';

  const renderAddressForm = () => (
    <>
      {error ? <div className={styles.checkoutAddressError}>{error}</div> : null}

      <Form onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Full Name *</Form.Label>
              <Form.Control name="FullName" value={form.FullName} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Mobile Number *</Form.Label>
              <Form.Control name="MobileNumber" value={form.MobileNumber} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Address Line 1 *</Form.Label>
              <Form.Control name="AddressLine1" value={form.AddressLine1} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control name="AddressLine2" value={form.AddressLine2} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Landmark</Form.Label>
              <Form.Control name="Landmark" value={form.Landmark} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Address Type</Form.Label>
              <Form.Select name="AddressType" value={form.AddressType} onChange={handleChange}>
                <option value="Home">Home</option>
                <option value="Work">Work</option>
                <option value="Other">Other</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>City *</Form.Label>
              <Form.Control name="City" value={form.City} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>State *</Form.Label>
              <Form.Control name="State" value={form.State} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Postal Code *</Form.Label>
              <Form.Control name="PostalCode" value={form.PostalCode} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Country *</Form.Label>
              <Form.Control name="Country" value={form.Country} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              name="IsDefault"
              label="Set as default address"
              checked={form.IsDefault}
              onChange={handleChange}
            />
          </Col>
        </Row>

        <div className={styles.checkoutAddressActions}>
          <Button variant="outline-secondary" type="button" onClick={handleBackFromForm}>
            {savedAddresses.length > 0 && !isEditMode
              ? 'Back to Saved Addresses'
              : isAccountMode
                ? 'Back to Home'
                : 'Back to Cart'}
          </Button>
          <Button variant="dark" type="submit" disabled={saving || loadingAddress}>
            {saving
              ? 'Saving...'
              : isAccountMode
                ? isEditMode
                  ? 'Update Address'
                  : 'Save Address'
                : isEditMode
                  ? 'Update & Review Order'
                  : 'Save & Review Order'}
          </Button>
        </div>
      </Form>
    </>
  );

  const renderAddressList = () => (
    <>
      {error ? <div className={styles.checkoutAddressError}>{error}</div> : null}

      <div className={styles.checkoutAddressListToolbar}>
        <p className={styles.checkoutAddressListHint}>
          {isAccountMode ? 'Your saved addresses.' : 'Select a delivery address to continue.'}
        </p>
        <button type="button" className={styles.checkoutAddressAddBtn} onClick={handleAddNew}>
          <FiPlus aria-hidden />
          Add New Address
        </button>
      </div>

      <div className={styles.checkoutAddressList} role="radiogroup" aria-label="Saved delivery addresses">
        {savedAddresses.map((address) => {
          const isSelected = selectedAddressId === address.addressId;
          const inputId = `checkout-address-${address.addressId}`;

          return (
            <div
              key={address.addressId}
              className={`${styles.checkoutAddressPickCard} ${isSelected ? styles.checkoutAddressPickCardSelected : ''}`}
              onClick={() => setSelectedAddressId(address.addressId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedAddressId(address.addressId);
                }
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
            >
              <div className={styles.checkoutAddressPickTop}>
                <input
                  id={inputId}
                  type="radio"
                  name="checkoutAddress"
                  className={styles.checkoutAddressPickRadio}
                  checked={isSelected}
                  onChange={() => setSelectedAddressId(address.addressId)}
                  onClick={(e) => e.stopPropagation()}
                />
                <label htmlFor={inputId} className={styles.checkoutAddressPickName}>
                  {address.fullName || '—'}
                </label>
                <button
                  type="button"
                  className={styles.checkoutAddressPickEdit}
                  onClick={(e) => handleEditAddress(e, address.addressId)}
                >
                  EDIT
                </button>
              </div>

              <p className={styles.checkoutAddressPickText}>{formatAddressSummary(address)}</p>
              <p className={styles.checkoutAddressPickPhone}>{address.mobileNumber || '—'}</p>

              {!isAccountMode ? (
                <button
                  type="button"
                  className={styles.checkoutAddressPickDeliver}
                  onClick={(e) => handleDeliverAddress(e, address.addressId)}
                >
                  Deliver to this Address
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className={styles.checkoutAddressListActions}>
        <Button
          variant="outline-secondary"
          type="button"
          onClick={() => navigate(isAccountMode ? '/' : '/cart')}
        >
          {isAccountMode ? 'Back to Home' : 'Back to Cart'}
        </Button>
      </div>
    </>
  );

  return (
    <section className={styles.checkoutAddressPage}>
      <Container>
        <div className={styles.checkoutAddressHeader}>
          <h1 className={styles.checkoutAddressTitle}>{pageTitle}</h1>
          <p className={styles.checkoutAddressSub}>{pageSub}</p>
        </div>

        <div className={styles.checkoutAddressCard}>
          {loadingList || loadingAddress ? (
            <div>Loading...</div>
          ) : showForm ? (
            renderAddressForm()
          ) : (
            renderAddressList()
          )}
        </div>
      </Container>
    </section>
  );
};

export default CheckoutAddressPage;
